'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Carousel, Container } from 'react-bootstrap';

import { urlForImage } from '@/sanity/lib/utils';
import type { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import './ResourcesSection.scss';

type LandingPageData = Extract<
  NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>,
  { _type: 'landingPage' }
>;

interface ResourcesSectionProps {
  data: NonNullable<LandingPageData['resources']>;
}

type ScreenSize = 'mobile' | 'tablet' | 'desktop';

// Time to wait after the last scroll event before silently re-centering a
// loop clone onto its real counterpart, so the wrap-around stays invisible.
const PEEK_SCROLL_SETTLE_DELAY = 120;

function getPeekMetrics(track: HTMLDivElement) {
  const item = track.children[0] as HTMLElement | undefined;
  if (!item) return null;

  const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
  const itemWidth = item.offsetWidth;

  return {
    step: itemWidth + gap,
    centerOffset: (track.offsetWidth - itemWidth) / 2,
  };
}

function centerPeekChild(
  track: HTMLDivElement,
  childIndex: number,
  behavior: ScrollBehavior
) {
  const child = track.children[childIndex] as HTMLElement | undefined;
  const metrics = getPeekMetrics(track);
  if (!child || !metrics) return;

  track.scrollTo({ left: child.offsetLeft - metrics.centerOffset, behavior });
}

export default function ResourcesSection({ data }: ResourcesSectionProps) {
  const { sectionLabel, resourceCards, backgroundColor } = data;

  const validCards = (resourceCards ?? []).filter(
    (
      resource
    ): resource is NonNullable<typeof resource> & {
      slug: { current: string };
    } => resource != null && resource.slug?.current != null
  );

  const [screenSize, setScreenSize] = useState<ScreenSize>('desktop');
  const [activePeekIndex, setActivePeekIndex] = useState(0);
  const peekTrackRef = useRef<HTMLDivElement>(null);
  const peekScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activePeekIndexRef = useRef(activePeekIndex);

  useEffect(() => {
    activePeekIndexRef.current = activePeekIndex;
  }, [activePeekIndex]);

  // Cards loop (last peeks in before the first, first peeks in after the
  // last) whenever there's more than one card to swipe between.
  const loopPeek = validCards.length > 1;
  const cloneOffset = loopPeek ? 1 : 0;

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      if (width <= 575) setScreenSize('mobile');
      else if (width <= 991) setScreenSize('tablet');
      else setScreenSize('desktop');
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  // Card widths are percentage-based, so keep the active card centered
  // (instantly, no animation) both on first entering tablet mode and on any
  // resize while already in it — otherwise a stale pixel scroll position
  // drifts off-center as the track's width changes.
  useEffect(() => {
    if (screenSize !== 'tablet') return;
    const track = peekTrackRef.current;
    if (!track) return;

    const recenter = () => {
      centerPeekChild(track, activePeekIndexRef.current + cloneOffset, 'auto');
    };

    const frame = requestAnimationFrame(recenter);
    const observer = new ResizeObserver(recenter);
    observer.observe(track);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [screenSize, cloneOffset]);

  useEffect(
    () => () => {
      if (peekScrollTimeout.current) clearTimeout(peekScrollTimeout.current);
    },
    []
  );

  if (validCards.length === 0) return null;

  const count = validCards.length;

  const handlePeekScroll = () => {
    const track = peekTrackRef.current;
    const metrics = track ? getPeekMetrics(track) : null;
    if (!track || !metrics || !metrics.step) return;

    const rawIndex = Math.round(
      (track.scrollLeft + metrics.centerOffset) / metrics.step
    );
    const realIndex = (((rawIndex - cloneOffset) % count) + count) % count;
    setActivePeekIndex(realIndex);

    if (!loopPeek) return;

    if (peekScrollTimeout.current) clearTimeout(peekScrollTimeout.current);
    peekScrollTimeout.current = setTimeout(() => {
      if (rawIndex === 0) {
        centerPeekChild(track, count, 'auto');
      } else if (rawIndex === count + 1) {
        centerPeekChild(track, 1, 'auto');
      }
    }, PEEK_SCROLL_SETTLE_DELAY);
  };

  const scrollToPeekCard = (index: number) => {
    const track = peekTrackRef.current;
    if (!track) return;

    centerPeekChild(track, index + cloneOffset, 'smooth');
  };

  const renderCard = (
    resource: (typeof validCards)[number],
    tabIndex?: number
  ) => {
    const image =
      resource.coverMedia?.type === 'video'
        ? resource.coverMedia?.thumbnail
        : resource.coverMedia?.coverImage;

    return (
      <Link
        href={`/resources/${resource.slug.current}`}
        className="rlp-resource-card"
        tabIndex={tabIndex}
      >
        {image && (
          <div className="rlp-resource-image">
            <Image
              src={urlForImage(image)?.url() as string}
              alt={image.alt || resource.title}
              width={287}
              height={146}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
          </div>
        )}
        <h3>{resource.title}</h3>
      </Link>
    );
  };

  const peekItems = loopPeek
    ? [
        {
          resource: validCards[count - 1],
          key: 'clone-start',
          isClone: true,
        },
        ...validCards.map((resource) => ({
          resource,
          key: resource._id,
          isClone: false,
        })),
        { resource: validCards[0], key: 'clone-end', isClone: true },
      ]
    : validCards.map((resource) => ({
        resource,
        key: resource._id,
        isClone: false,
      }));

  return (
    <section
      className="rlp-resources-section"
      style={
        backgroundColor?.value ? { backgroundColor: backgroundColor.value } : {}
      }
    >
      <Container>
        {sectionLabel && (
          <span className="rlp-section-label">{sectionLabel}</span>
        )}
        {screenSize === 'mobile' && (
          <Carousel
            className="rlp-carousel rlp-resource-carousel"
            indicators={validCards.length > 1}
            controls={false}
            interval={3000}
            pause="hover"
          >
            {validCards.map((resource) => (
              <Carousel.Item key={resource._id}>
                {renderCard(resource)}
              </Carousel.Item>
            ))}
          </Carousel>
        )}

        {screenSize === 'tablet' && (
          <div className="rlp-resource-peek">
            <div
              className="rlp-resource-peek-track"
              ref={peekTrackRef}
              onScroll={handlePeekScroll}
            >
              {peekItems.map(({ resource, key, isClone }) => (
                <div
                  className="rlp-resource-peek-item"
                  key={key}
                  aria-hidden={isClone || undefined}
                >
                  {renderCard(resource, isClone ? -1 : undefined)}
                </div>
              ))}
            </div>

            {validCards.length > 1 && (
              <div className="rlp-resource-peek-dots">
                {validCards.map((resource, index) => (
                  <button
                    key={resource._id}
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                    className={index === activePeekIndex ? 'active' : ''}
                    onClick={() => scrollToPeekCard(index)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {screenSize === 'desktop' && (
          <div className="rlp-resource-cards">
            {validCards.map((resource) => (
              <div key={resource._id}>{renderCard(resource)}</div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}
